import { useCallback, useEffect, useState } from "react";
import { fetchJson } from "../../api/rest";
import type { CategoryInfo, NodeCatalogue, NodeDef } from "../../types/flows";

// The catalogue is the same for every page and never changes between requests,
// so it is fetched once per browser session and shared. Every page that renders
// a node — palette, canvas, template gallery, run inspector — needs it, and
// re-fetching 50 kB of schemas on each navigation was measurable.

let cached: NodeCatalogue | null = null;
let inflight: Promise<NodeCatalogue> | null = null;

export const loadCatalogue = (): Promise<NodeCatalogue> => {
	if (cached) return Promise.resolve(cached);
	if (!inflight) {
		inflight = fetchJson<NodeCatalogue>("/flows/nodes").then((data) => {
			cached = data;
			inflight = null;
			return data;
		});
	}
	return inflight;
};

export interface CatalogueIndex {
	catalogue: NodeCatalogue | null;
	error: string | null;
	node: (type: string) => NodeDef | undefined;
	category: (id: string) => CategoryInfo | undefined;
	/** The accent of the node's category — what the canvas colours a node with. */
	accentFor: (type: string) => string;
}

export const useCatalogue = (): CatalogueIndex => {
	const [catalogue, setCatalogue] = useState<NodeCatalogue | null>(cached);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (cached) return;
		loadCatalogue()
			.then(setCatalogue)
			.catch((e) => setError(String(e)));
	}, []);

	const node = useCallback(
		(type: string) => catalogue?.nodes.find((n) => n.type === type),
		[catalogue],
	);
	const category = useCallback(
		(id: string) => catalogue?.categories.find((c) => c.id === id),
		[catalogue],
	);
	const accentFor = useCallback(
		(type: string) => {
			const def = catalogue?.nodes.find((n) => n.type === type);
			return (
				catalogue?.categories.find((c) => c.id === def?.category)?.accent ??
				"slate"
			);
		},
		[catalogue],
	);

	return { catalogue, error, node, category, accentFor };
};

/**
 * A one-line description of what a node is configured to do, for the canvas.
 *
 * Deliberately generic: it reads the config object rather than knowing anything
 * about individual node types, so a new node summarises sensibly on day one.
 */
export function configSummary(
	def: NodeDef | undefined,
	config: Record<string, unknown> | undefined,
): string {
	if (!config || Object.keys(config).length === 0) {
		return def ? "Not configured yet" : "Unknown node";
	}
	const parts: string[] = [];
	for (const [key, value] of Object.entries(config)) {
		if (value === null || value === undefined || value === "") continue;
		if (Array.isArray(value)) {
			if (value.length > 0) parts.push(`${key}: ${value.length}`);
			continue;
		}
		if (typeof value === "object") continue;
		if (typeof value === "boolean") {
			if (value) parts.push(key);
			continue;
		}
		const text = String(value);
		parts.push(`${key}: ${text.length > 28 ? `${text.slice(0, 28)}…` : text}`);
	}
	return parts.length > 0
		? parts.slice(0, 3).join(" · ")
		: "Not configured yet";
}
